import type { EarmarkAccount, GoalResponse } from '@/api/goals'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { PANEL } from '@/components/budget/MonthlyBudgets'
import { cn } from '@/lib/utils'

/** For each account holding goal money: how much is set aside and how much is free (or short). */
export function WhereItSits({
  accounts,
  activeGoals,
}: {
  accounts: EarmarkAccount[]
  activeGoals: GoalResponse[]
}) {
  const { formatWhole } = useFormatCurrency()
  const ownAccounts = [
    ...new Map(
      activeGoals
        .filter((g) => g.trackingMode === 'DEDICATED')
        .map((g) => [g.linkedAccountId, g.linkedAccountName] as const)
    ).values(),
  ]
  if (accounts.length === 0 && ownAccounts.length === 0) return null

  return (
    <section aria-label="Where it sits" className={PANEL}>
      <div className="flex items-baseline justify-between gap-3 pb-1 pt-3.5">
        <h2 className="text-[15px] font-semibold text-[var(--fl-text-1)]">Where it sits</h2>
        <span className="text-xs text-[var(--fl-text-2)]">Set aside vs free</span>
      </div>
      <ul className="divide-y divide-[var(--fl-divider)]">
        {accounts.map((a) => {
          const short = a.free < 0
          const names = activeGoals
            .filter((g) => g.trackingMode === 'EARMARK' && g.linkedAccountId === a.accountId)
            .map((g) => g.name)
          const setAsideShare = a.balance > 0 ? Math.min(a.earmarked / a.balance, 1) : 1
          return (
            <li key={a.accountId} className="py-3 text-[13.5px]">
              <div className="flex justify-between gap-3">
                <span className="truncate font-medium text-[var(--fl-text-1)]">
                  {a.accountName}
                </span>
                <span className="shrink-0 tabular-nums text-[var(--fl-text-1)]">
                  {formatWhole(a.balance)}
                </span>
              </div>
              <div
                aria-hidden="true"
                className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-[var(--fl-surface-subtle)]"
              >
                <div
                  className={short ? 'bg-[var(--fl-error)]' : 'bg-[var(--fl-hero-bar)]'}
                  style={{ width: `${setAsideShare * 100}%` }}
                />
              </div>
              <div
                className={cn(
                  'mt-1 flex justify-between gap-3 text-xs tabular-nums',
                  short ? 'text-[var(--fl-error)]' : 'text-[var(--fl-text-2)]'
                )}
              >
                <span className="min-w-0 truncate">
                  {formatWhole(a.earmarked)} for {names.join(', ') || 'goals'}
                </span>
                <span className="shrink-0">
                  {short ? `${formatWhole(-a.free)} short` : `${formatWhole(a.free)} free`}
                </span>
              </div>
            </li>
          )
        })}
        {ownAccounts.length > 0 && (
          <li className="py-3 text-[13.5px]">
            <span className="font-medium text-[var(--fl-text-1)]">{ownAccounts.join(' · ')}</span>
            <p className="mt-0.5 text-xs text-[var(--fl-text-2)]">
              Whole balance counts toward its goal
            </p>
          </li>
        )}
      </ul>
    </section>
  )
}
